import { createSelector } from "reselect";
import { feedEntitiesWithGeoByTypeSelector } from "orion-components/GlobalData/Selectors";
import { getAllDevicesSelector } from "../DeviceGroups/selectors";
import cloneDeep from "lodash/cloneDeep";

const compositeDeviceSelector = (state) =>
	state?.globalData?.compositeDevices ? Object.values(state.globalData.compositeDevices) : [];

const deviceTypeSelector = (state) => state?.globalData?.deviceTypes ?? {};

export const getAllCompositeDeviceSelector = createSelector(
	[compositeDeviceSelector, deviceTypeSelector],
	(compositeDevices = [], deviceTypes) => {
		const result = [];
		compositeDevices.forEach((device) => {
			result.push({
				id: device?.id,
				deviceName: device?.name,
				deviceType: deviceTypes[device?.deviceType]?.name,
				icon: deviceTypes[device?.deviceType]?.icon
			});
		});
		return result;
	}
);

const camerasSelector = (state) => {
	const cameras = feedEntitiesWithGeoByTypeSelector("camera")(state);
	return cameras ? cameras : {};
};

const devicesSelector = (state) => {
	const devices = feedEntitiesWithGeoByTypeSelector("device")(state);
	return devices ? devices : {};
};

const deviceStateSelector = (state) => state?.globalData?.deviceState;

const getCompositeDeviceStateColorPriority = (devices) => {
	if (!devices || devices.length === 0) return null;
	const highPriorityDevice = devices.reduce((min, device) => {
		return device.deviceStatePriority < min.deviceStatePriority ? device : min;
	});

	return highPriorityDevice.deviceStateColor;
};

export const selectCompositeDeviceStateColors = createSelector(
	[camerasSelector, devicesSelector, deviceStateSelector],
	(cameras, devices, deviceState) => {
		const allDevices = { ...cameras, ...devices };

		// Group devices by compositeDeviceId
		// The following logic will group devices with their composite devices.
		const groupedByCompositeDevices = Object.values(allDevices).reduce((acc, entity) => {
			const { compositeDeviceId } = entity;
			if (compositeDeviceId) {
				if (!acc[compositeDeviceId]) {
					acc[compositeDeviceId] = [];
				}
				acc[compositeDeviceId].push({
					...entity,
					deviceStateColor: deviceState?.[entity?.entityData?.properties?.deviceState]?.color,
					deviceStatePriority: deviceState?.[entity?.entityData?.properties?.deviceState]?.priority
				});
			}
			return acc;
		}, {});

		// Compute the highest priority device state color for each composite device
		const compositeDeviceStateColors = {};
		Object.entries(groupedByCompositeDevices).forEach(([compositeDeviceId, devices]) => {
			compositeDeviceStateColors[compositeDeviceId] = getCompositeDeviceStateColorPriority(devices);
		});
		return compositeDeviceStateColors;
	}
);

export const selectDevicesByCompositeDeviceId = (state, compositeDeviceId, ignoreCameras = false) => {
	const cameras = camerasSelector(state);
	const devices = devicesSelector(state);

	// Merge only devices if ignoreCameras is true
	const allDevices = ignoreCameras ? { ...devices } : { ...cameras, ...devices };

	// Filter devices that match the given compositeDeviceId
	const matchingDevices = Object.values(allDevices).filter(
		(device) => device.compositeDeviceId === compositeDeviceId
	);

	return matchingDevices;
};

export const getCompositeDeviceSelector = createSelector(
	[
		(state, props) => getAllDevicesSelector(state, props),
		(_, props) => props?.compositeDeviceId // Extract compositeDeviceId
	],
	(allDevices, compositeDeviceId) => {
		if (!compositeDeviceId) return null;

		// Find the composite device
		const compositeDevice = allDevices[compositeDeviceId];
		if (!compositeDevice || compositeDevice.entityType !== "compositeDevice") return null;

		const children = [];
		const deviceCommands = [];
		const devicesAndCameras = cloneDeep(allDevices);

		// Find children belonging to this composite device
		for (const uuid in devicesAndCameras) {
			const data = devicesAndCameras[uuid];
			if (data.compositeDeviceId === compositeDeviceId) {
				const deviceType = data.deviceType;
				data.isDevice = true;
				data.isGroup = false;
				data.name = data?.entityData?.properties?.name;
				data.deviceType = deviceType || null;
				data.useCustomMargin = true;
				data.customMargin = 24;

				// Collect children
				children.push(data);

				// Collect commands
				const commands = data?.entityData?.properties?.commands;
				if (commands) {
					const filteredCommands = commands
						.flat()
						.filter((item) => typeof item === "object" && !Array.isArray(item))
						.map((command) => ({
							...command,
							entityIds: [data?.id]
						}));

					deviceCommands.push(...filteredCommands);
				}
			}
		}

		return [
			{
				...compositeDevice,
				children,
				deviceCommands: deviceCommands.length ? deviceCommands : null,
				deviceStateColor: getCompositeDeviceStateColorPriority(children),
				isDevice: true
			}
		];
	}
);
